import apiClient from '@/services/apiClient'
import type { Subscription, HolidayRequest, LineAmendment, DeliveryOverlay } from '@/types'

/**
 * DF-345: the read-only delivery view embedded in Dynamics via an iframe.
 *
 * Staff are signed in with their Microsoft token (option 3): the Dynamics form
 * passes it after the # in the iframe address, the page swaps it for a staff
 * session, and every deliveries request carries that session id. One call
 * returns everything the delivery calendar needs for a contact.
 */
export interface CrmDeliveriesResponse {
  customer: { name: string, delivery_days: number[] }
  subscriptions: Subscription[]
  holidays: HolidayRequest[]
  amendments: LineAmendment[]
  overlay: DeliveryOverlay
}

/** Must match the backend's CrmViewerSessions::HEADER. */
const SESSION_HEADER = 'X-CRM-Viewer-Session'

interface Wrapped<T> { data?: T }

const crmService = {
  /** Swap the staff member's Microsoft token for a Portal staff session. */
  async startSession(token: string): Promise<{ sessionId: string, expiresAt: number }> {
    // Not a customer save, so no "Saving your change" overlay.
    const response = await apiClient.post('/api/crm/session', { token }, { skipMutationLock: true, withoutCustomerSession: true })
    const data = (response as unknown as Wrapped<{ session_id: string, expires_at: number }>).data
    if (!data?.session_id) throw new Error('No session returned')
    return { sessionId: data.session_id, expiresAt: data.expires_at }
  },

  async getDeliveries(contact: string, dataHash: string | null, sessionId: string | null): Promise<CrmDeliveriesResponse> {
    const query = new URLSearchParams({ contact })
    if (dataHash) query.set('data_hash', dataHash)

    const response = await apiClient.get(`/api/crm/deliveries?${query.toString()}`, {
      headers: sessionId ? { [SESSION_HEADER]: sessionId } : {},
      withoutCustomerSession: true,
    })
    const data = (response as unknown as Wrapped<CrmDeliveriesResponse>).data
    if (!data) throw new Error('No delivery data returned')
    return data
  },
}

export default crmService
